import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'

import * as ecModels from '../../../models'

import type { Plugin } from './plugin'

export class Selectors<TTypeParams extends ecModels.TypeParams.Constraint> {
  /**
   * A selection that represents a list of entity IDs that compose the current
   * list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public ids

  /**
   * A selection that represents a list of entities that compose the current
   * list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public entities

  /**
   * First entity ID in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public firstId

  /**
   * First entity in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public firstEntity

  /**
   * Last entity ID in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public lastId

  /**
   * Last entity in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public lastEntity

  /**
   * Position of the given ID in the current list
   *
   * Returns `undefined` if the ID is not in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public getPositionOf

  /**
   * Entity ID that immediately follows the given ID in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public getIdAfter

  /**
   * Entity that immediately follows the entity with the given ID in the current
   * list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public getEntityAfter

  /**
   * Entity ID that immediately precedes the given ID in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public getIdBefore

  /**
   * Entity that immediately precedes the entity with the given ID in the
   * current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public getEntityBefore

  /**
   * Number of entities that match the applied params
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public matchedCount

  /**
   * Represents the portion of {@link queryRequest} that affects
   * {@link matchedCount}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public queryRequestThatAffectsMatchedCount

  /**
   * Represents the portion of {@link queryRequest} that includes everything but
   * pagination
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public queryRequestWithoutPagination

  /**
   * The {@link ecModels.QueryRequest} that controls what is in the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public queryRequest

  /**
   * Indicates whether the current list parameters are being edited
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public isEditingParams

  /**
   * A selection that represents a flag that indicates whether or not the
   * current list is loading
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public isLoading

  /**
   * A selection that represents an error that might have occurred when loading
   * the current list
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public error

  constructor(plugin: Plugin<TTypeParams>) {
    this.ids = plugin.feature.state.project(state => state.ids)

    const cachedEntitiesLookupSelector =
      plugin.collection.plugins.cachePlugin.feature.selectors.lookup.asSelector

    this.entities = this.ids.project(
      cachedEntitiesLookupSelector,
      (ids, lookup) =>
        ids.map(id => lookup[id]).filter(TypeChecker.isNotNullish.raw),
    )

    // NOTE: these need an explicit return type since TypeScript doesn't
    // recognize the possibility for undefined
    this.firstId = this.ids.project(
      (ids): (typeof ids)[number] | undefined => ids[0],
    )
    this.lastId = this.ids.project(
      (ids): (typeof ids)[number] | undefined => ids[ids.length - 1],
    )
    this.firstEntity = this.firstId.project(
      cachedEntitiesLookupSelector,
      (id, lookup) => (id === undefined ? undefined : lookup[id]),
    )
    this.lastEntity = this.lastId.project(
      cachedEntitiesLookupSelector,
      (id, lookup) => (id === undefined ? undefined : lookup[id]),
    )

    this.getPositionOf = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        this.ids.project(ids => {
          const idx = ids.indexOf(id)
          // -1 means the id was not found. We treat that as `undefined`
          return idx === -1 ? undefined : idx
        }).asSelector,
    )
    this.getIdAfter = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        store.selectors.Selector.create(
          this.ids.asSelector,
          this.getPositionOf(id).asSelector,
          (ids, idx): ecModels.EntityId | undefined =>
            idx === undefined ? undefined : ids[idx + 1],
        ),
    )
    this.getIdBefore = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        store.selectors.Selector.create(
          this.ids.asSelector,
          this.getPositionOf(id).asSelector,
          (ids, idx): ecModels.EntityId | undefined =>
            idx === undefined ? undefined : ids[idx - 1],
        ),
    )
    this.getEntityAfter = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        store.selectors.Selector.create(
          this.getIdAfter(id).asSelector,
          cachedEntitiesLookupSelector,
          (nextId, lookup) =>
            nextId === undefined ? undefined : lookup[nextId],
        ),
    )
    this.getEntityBefore = plugin.feature.createSelectorMethod(
      (id: ecModels.EntityId) =>
        store.selectors.Selector.create(
          this.getIdBefore(id).asSelector,
          cachedEntitiesLookupSelector,
          (nextId, lookup) =>
            nextId === undefined ? undefined : lookup[nextId],
        ),
    )

    // TODO: -
    this.queryRequestThatAffectsMatchedCount =
      plugin.appliedParams.createSelector(() =>
        store.selectors.Selector.create(
          plugin.appliedParams.selectors.filters.asSelector,
          plugin.appliedParams.selectors.search.asSelector,
          (additionalFilters, search) =>
            ({
              filter: { additionalFilters, searchTerm: search },
            }) satisfies ecModels.QueryRequest<TTypeParams>,
        ),
      )

    this.queryRequestWithoutPagination = plugin.appliedParams.createSelector(
      () =>
        store.selectors.Selector.create(
          this.queryRequestThatAffectsMatchedCount.asSelector,
          plugin.appliedParams.selectors.descending.asSelector,
          plugin.appliedParams.selectors.sortBy.asSelector,
          (queryRequest, descending, sortBy) =>
            ({
              ...queryRequest,
              sort:
                sortBy !== undefined ? { by: sortBy, descending } : undefined,
            }) satisfies ecModels.QueryRequest<TTypeParams>,
        ),
    )

    this.queryRequest = plugin.appliedParams.createSelector(() =>
      store.selectors.Selector.create(
        this.queryRequestWithoutPagination.asSelector,
        plugin.appliedParams.selectors.quantity.asSelector,
        plugin.appliedParams.selectors.skip.asSelector,
        (queryRequest, quantity, skip) =>
          ({
            ...queryRequest,
            pagination: { quantity, skip },
          }) satisfies ecModels.QueryRequest<TTypeParams>,
      ),
    )

    this.matchedCount = plugin.feature.state.project(
      this.queryRequest.asSelector,
      this.ids.asSelector,
      (state, { pagination }, entityIds) => {
        if (state.matchedCount !== undefined) {
          return state.matchedCount
        }
        const apiHasCount =
          plugin.collection.plugins.apiExecutionPlugin.canExecute('count')
        const listMethodRef = plugin.collection.api.list
        const appliedPagination =
          ents.core.common.Request.For.Pagination.ByPosition.isPaginated(
            pagination,
          )

        // The current list plugin should use the length of the list as the
        // matchedCount in case when these conditions are met:
        // - count API method is not implemented
        // - list API method is implemented
        // - applied params do not include pagination
        //   TODO: Should be checked if list api method does not support
        //   pagination. Probably should be done through an ExtraReducer on the
        //   execute/success action from the api-execution plugin. Basically, if
        //   count is not implemented and pagination is not supported, then when
        //   we get a successful list response, we should write the length of
        //   that list to matchedCount in the state.
        if (apiHasCount || !listMethodRef || appliedPagination) {
          // Returned undefined since we already checked and returned matched
          // count if it is defined, and in this case we don't have anything to
          // return
          return undefined
        }

        const { pagination: listSupportPagination } =
          ecModels.Api.list.QueryRequestUsage.from(listMethodRef)

        if (listSupportPagination) {
          return state.matchedCount
        }

        return entityIds.length
      },
    )

    this.isEditingParams = plugin.feature.state.project(
      state => state.isEditingParams,
    )
    this.isLoading = plugin.feature.state.project(state => state.isLoading)
    this.error = plugin.feature.state.project(state => state.error)
  }
}
